import {classes} from '@bitstillery/common/lib/utils'
import {Changed, Icon} from '@bitstillery/common/components/portal'
import {MithrilComponent} from '@bitstillery/mithril'
import {$t} from '@bitstillery/common/app'
import m from '@bitstillery/mithril'
import {CartValidation} from '@bitstillery/common/api/fact2server'
import {format_money_with_symbol} from '@bitstillery/common/lib/format'
import {events} from '@bitstillery/common/app'

import type {CartValidationError} from '@/lib/state'
import type {CartItemLookup, CartOfferItemLookup} from '@/models/cart'
import {filters} from '@/components/pages/offers/offer_list'
import {is_string_list} from '@/lib/filter_selection'
import {CartItemActions} from '@/components/panels/panel_cart/cart_item_actions'
import {$m, $s} from '@/app'

export type CartErrorsAttrs = Record<string, never>

export class CartErrors extends MithrilComponent<CartErrorsAttrs> {
    view(_vnode: m.Vnode<CartErrorsAttrs>) {
        // Group quantity and price related errors into separate blocks.
        // E.g. error-category-group > error-category > errors
        const error_category_groups = (
            [
                [
                    'PRICE',
                    {
                        ...$s.cart.errors[CartValidation.PRICE_WAS_RAISED],
                    },
                ],
                [
                    'QUANTITY',
                    {
                        ...$s.cart.errors[CartValidation.QUANTITY],
                        ...$s.cart.errors[CartValidation.MIN_QUANTITY],
                        ...$s.cart.errors[CartValidation.STEP_SIZE],
                    },
                ],
            ] as ['PRICE' | 'QUANTITY', Record<string, CartValidationError>][]
        ).filter((i) => Object.keys(i[1]).length)

        return (
            <div
                className={classes('c-cart-errors', {
                    collapsed: $s.panels.context.collapsed,
                })}
            >
                {error_category_groups.map(
                    ([error_category_group, error_category]: ['PRICE' | 'QUANTITY', Record<string, CartValidationError>]) => {
                        const errors = Object.values(error_category)
                        // Ensure unavailable products (valid_value === 0) are listed on top for QUANTITY group
                        if (error_category_group === 'QUANTITY') {
                            errors.sort((a, b) => {
                                const a_zero = a.valid_value === 0 ? 1 : 0
                                const b_zero = b.valid_value === 0 ? 1 : 0
                                if (b_zero - a_zero !== 0) {
                                    return b_zero - a_zero
                                }
                                const cart_items: CartItemLookup = $s.cart.items
                                const a_item = cart_items[a.case_artkey]
                                const b_item = cart_items[b.case_artkey]
                                const a_name = a_item ? a_item.product_name : ''
                                const b_name = b_item ? b_item.product_name : ''
                                return a_name.localeCompare(b_name)
                            })
                        }

                        return (
                            <div className='category'>
                                <div className='break-line warning'>
                                    <Icon
                                        name={error_category_group === 'QUANTITY' ? 'cart_off' : 'arrowUpCircle'}
                                        type='unset'
                                        tip={$t('cart.issues_action')}
                                    />

                                    <span>
                                        {$t(`cart.error_category.header.${error_category_group}`, {
                                            count: errors.length,
                                            units: $t(`unit.${$s.identity.user.price_preference}`, {count: 1}),
                                        })}
                                    </span>
                                </div>
                                <div className='body'>
                                    {errors.map((error) => {
                                        const offer_items: CartOfferItemLookup = $s.cart.offer_items
                                        const offer_item = offer_items[error.case_artkey]
                                        // < 0 should not happen, but just in case (otherwise we may crash without an offer_item).
                                        const is_out_of_stock =
                                            !offer_item || (error_category_group === 'QUANTITY' && error.valid_value <= 0)

                                        const cart_items: CartItemLookup = $s.cart.items
                                        const cart_item = cart_items[error.case_artkey]
                                        if (!cart_item) {
                                            return
                                        }

                                        const i18n_params: Record<string, number | string | null> = {}
                                        let valid_amount, invalid_amount

                                        // This is an unavailable item; it may (still in the browser state, while it was available) or may not
                                        // (has been removed from the offer) have an offer_item.
                                        if (is_out_of_stock) {
                                            i18n_params.valid_value = $m.offer.unit_amount(
                                                error.valid_value,
                                                cart_item.case_number_of_bottles,
                                            )
                                            i18n_params.invalid_value = $m.offer.unit_amount(
                                                error.invalid_value,
                                                cart_item.case_number_of_bottles,
                                            )
                                            const unit_price = $m.offer.unit_price(
                                                cart_item.list_price,
                                                cart_item.case_number_of_bottles,
                                            )
                                            valid_amount = i18n_params.valid_value * unit_price
                                            invalid_amount = i18n_params.invalid_value * unit_price
                                        } else if (error_category_group === 'QUANTITY') {
                                            // (!) Use the offer_item list_price for the unit price, so that the price stays reactive.
                                            if (error.validation_error === CartValidation.MIN_QUANTITY) {
                                                i18n_params.min_quantity = $m.offer.unit_amount(
                                                    error.minimum_quantity,
                                                    cart_item.case_number_of_bottles,
                                                )
                                            } else if (error.validation_error === CartValidation.STEP_SIZE) {
                                                i18n_params.step_size = $m.offer.unit_amount(
                                                    error.step_size_quantity,
                                                    cart_item.case_number_of_bottles,
                                                )
                                            }
                                            i18n_params.valid_value = $m.offer.unit_amount(
                                                error.valid_value,
                                                cart_item.case_number_of_bottles,
                                            )
                                            i18n_params.invalid_value = $m.offer.unit_amount(
                                                error.invalid_value,
                                                cart_item.case_number_of_bottles,
                                            )
                                            const unit_price = $m.offer.unit_price(
                                                offer_item.list_price,
                                                cart_item.case_number_of_bottles,
                                            )
                                            valid_amount = i18n_params.valid_value * unit_price
                                            invalid_amount = i18n_params.invalid_value * unit_price
                                        } else {
                                            const cart_units = $m.offer.unit_amount(
                                                cart_item.number_of_cases,
                                                cart_item.case_number_of_bottles,
                                            )
                                            const invalid_value_units = $m.offer.unit_price(
                                                error.invalid_value,
                                                cart_item.case_number_of_bottles,
                                            )
                                            invalid_amount = invalid_value_units * cart_units
                                            i18n_params.invalid_value = format_money_with_symbol(
                                                invalid_value_units,
                                                offer_item.currency,
                                            )
                                            // (!) Use the offer_item list_price for the valid value, so that the price stays reactive.
                                            const valid_value_units = $m.offer.unit_price(
                                                offer_item.list_price,
                                                cart_item.case_number_of_bottles,
                                            )
                                            valid_amount = valid_value_units * cart_units
                                            i18n_params.valid_value = format_money_with_symbol(
                                                valid_value_units,
                                                offer_item.currency,
                                            )
                                        }

                                        let validation_error_icon, validation_error_tip

                                        if (is_out_of_stock) {
                                            validation_error_tip = $t('cart.error_category.action.UNAVAILABLE', {...i18n_params})
                                        } else {
                                            validation_error_tip = $t(`cart.error.${error.validation_error}`, {
                                                ...i18n_params,
                                                // Use count for tags with pluralisation; e.g. cart.error.QUANTITY_one
                                                count: i18n_params.valid_value,
                                                units: $t(`unit.${$s.identity.user.price_preference}`, {
                                                    count: i18n_params.valid_value,
                                                }),
                                            })
                                            validation_error_tip = `${validation_error_tip} ${$t(`cart.error_category.action.${error_category_group}`)}`
                                        }

                                        if (error.validation_error === CartValidation.PRICE_WAS_RAISED) {
                                            validation_error_icon = 'arrowUpCircle'
                                        } else {
                                            if (is_out_of_stock) {
                                                validation_error_icon = 'cartRemove'
                                            } else if (
                                                [CartValidation.STEP_SIZE, CartValidation.MIN_QUANTITY].includes(
                                                    error.validation_error,
                                                )
                                            ) {
                                                validation_error_icon = 'trolly'
                                            } else {
                                                validation_error_icon = 'stock'
                                            }
                                        }
                                        return (
                                            <div
                                                className={classes('product-line', {
                                                    danger: is_out_of_stock,
                                                    warning: !is_out_of_stock,
                                                })}
                                            >
                                                <Icon
                                                    className='quantity-type-icon'
                                                    name={validation_error_icon}
                                                    size='s'
                                                    tip={() => validation_error_tip}
                                                    type={is_out_of_stock ? 'danger' : 'warning'}
                                                />
                                                <div className='quantity'>
                                                    {error_category_group === 'QUANTITY' ? (
                                                        <Changed
                                                            className='fl-column-reverse gap-0'
                                                            current={Number(i18n_params.valid_value)}
                                                            format_as_number={true}
                                                            previous={Number(i18n_params.invalid_value)}
                                                            type='warning'
                                                        />
                                                    ) : (
                                                        <span>
                                                            {$m.offer.unit_amount(
                                                                cart_item.number_of_cases,
                                                                cart_item.case_number_of_bottles,
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                                <div
                                                    className='product-name'
                                                    onclick={() => {
                                                        if (m.route.get().startsWith('/shop/checkout')) {
                                                            return
                                                        }

                                                        filters.offertype.selection = ''
                                                        const terms = filters.search.selection
                                                        if (Array.isArray(terms) && is_string_list(terms)) {
                                                            terms.splice(0, terms.length, cart_item.product_name)
                                                        }
                                                        events.emit('search', filters.search.selection)
                                                    }}
                                                >
                                                    {cart_item.product_name}
                                                </div>

                                                <Changed
                                                    className='fl-column-reverse gap-0'
                                                    currency={$s.identity.user.currency}
                                                    current={valid_amount}
                                                    previous={invalid_amount}
                                                    type='warning'
                                                />
                                                <CartItemActions cart_item={cart_item} error={error} />
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )
                    },
                )}
            </div>
        )
    }
}
